'use client';

import { c as _c } from "react/compiler-runtime";
import { HousePerson, NavArrowDown, User } from '@byway/icons';
import { type CalendarDate, DateFormatter, getLocalTimeZone, today } from '@internationalized/date';
import { differenceInCalendarDays } from 'date-fns';
import dynamic from 'next/dynamic';
import { useLocale, useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Button } from '~/components/Button';
import { visuallyHidden } from '~/components/Utilities';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { trackClickEvent, trackFunnelEvent } from '~/utils/funnelTracking';
import { scrollClearOfStickyFooter } from '~/utils/scrollClearOfStickyFooter';
import { useTripDateSelection } from '../hooks/useTripDateSelection';
import { useTripOriginUpdate } from '../hooks/useTripOriginUpdate';
import { useTripOverviewSummary } from '../hooks/useTripOverviewSummary';
import { useTripTravellersUpdate } from '../hooks/useTripTravellersUpdate';
import { ChangeOriginModal } from './ChangeOriginModal';
import { ChangeTravellersModal } from './ChangeTravellersModal';
const RangeCalendar = dynamic(() => import('~/components/RangeCalendar').then(m => m.RangeCalendar), {
  ssr: false
});
import styles from './BookingDetails.module.css';
export function BookingDetails() {
  const $ = _c(146);
  const t = useTranslations("journey");
  const tForms = useTranslations("forms");
  const locale = useLocale();
  const timeZone = getLocalTimeZone();
  const isDesktop = useIsDesktop();
  const isTripUpdating = useIsTripUpdating();
  const {
    tripId,
    isReadOnly,
    isLiveChecking
  } = useTripOverviewSummary();
  const isEditingLocked = isReadOnly || isLiveChecking;
  const {
    localRange,
    setLocalRange,
    range,
    calendarKey,
    hasSelectedRange,
    originalDuration,
    datesConfirmed,
    confirmDates,
    clearDates
  } = useTripDateSelection();
  const {
    confirmedOrigin: selectedOrigin,
    confirmOrigin
  } = useTripOriginUpdate();
  const {
    selectedAdultCount,
    selectedChildCount,
    selectedRooms,
    adultCount,
    childAges,
    confirmTravellers
  } = useTripTravellersUpdate();
  const [isOriginModalOpen, setIsOriginModalOpen] = useState(false);
  const [isTravellersModalOpen, setIsTravellersModalOpen] = useState(false);
  const dateActionsRef = useRef(null);
  const detailFormatter = new DateFormatter(locale, {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
  const dateRangeStart = detailFormatter.format(range.start.toDate(timeZone));
  const dateRangeEnd = detailFormatter.format(range.end.toDate(timeZone));
  const tripDayCount = differenceInCalendarDays(range.end.toDate(timeZone), range.start.toDate(timeZone)) + 1;
  let t0;
  if ($[0] !== isDesktop || $[1] !== t) {
    t0 = isDesktop ? t("edit_dates_your") : t("booking_details_heading");
    $[0] = isDesktop;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = <h2 id="trip-v2-booking-heading" className={styles.heading}>{t0}</h2>;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t) {
    t2 = t("booking_origin_question");
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <p className={styles.question}>{t2}</p>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t) {
    t4 = t("booking_choose_origin");
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== setIsOriginModalOpen) {
    t5 = () => {
      trackClickEvent("trip-page-edit-click-origin");
      setIsOriginModalOpen(true);
    };
    $[11] = setIsOriginModalOpen;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className={styles.rowIcon} aria-hidden={true}><HousePerson decorative={true} size="large" /></span>;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== selectedOrigin) {
    t7 = <span className={styles.rowValue}>{selectedOrigin}</span>;
    $[14] = selectedOrigin;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isEditingLocked || $[17] !== t4 || $[18] !== t5 || $[19] !== t7) {
    t8 = <button type="button" className={styles.selectRow} aria-label={t4} disabled={isEditingLocked} onClick={t5}>{t6}{t7}</button>;
    $[16] = isEditingLocked;
    $[17] = t4;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t3 || $[22] !== t8) {
    t9 = <div className={`${styles.group} ${styles.desktopHidden}`}>{t3}{t8}</div>;
    $[21] = t3;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className={`${styles.divider} ${styles.desktopHidden}`} aria-hidden={true} />;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t) {
    t11 = t("booking_dates_question");
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11) {
    t12 = <p className={`${styles.question} ${styles.desktopDatesQuestionHidden}`}>{t11}</p>;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t || $[30] !== tripDayCount) {
    t13 = t("booking_trip_day_count", {
      count: tripDayCount
    });
    $[29] = t;
    $[30] = tripDayCount;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13) {
    t14 = <p className={styles.tripLength}>{t13}</p>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== dateRangeEnd || $[35] !== dateRangeStart || $[36] !== hasSelectedRange) {
    t15 = hasSelectedRange && <p className={styles.dateDetail}>{dateRangeStart}<span aria-hidden="true"> → </span><span className={visuallyHidden}> to </span>{dateRangeEnd}</p>;
    $[34] = dateRangeEnd;
    $[35] = dateRangeStart;
    $[36] = hasSelectedRange;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t) {
    t16 = t("booking_dates_calendar_label");
    $[38] = t;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const t17 = isDesktop ? 2 : 1;
  const t18 = isDesktop ? "horizontal" : "vertical";
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = today(getLocalTimeZone());
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const t20 = hasSelectedRange ? range : null;
  const t21 = originalDuration ?? undefined;
  let t22;
  if ($[41] !== isDesktop || $[42] !== originalDuration || $[43] !== setLocalRange) {
    t22 = next => {
      if (!next?.start || !next.end) {
        return;
      }
      trackFunnelEvent("trip_funnel_dates_calendar_select");
      const start = next.start as CalendarDate;
      const end = originalDuration != null ? start.add({
        days: originalDuration - 1
      }) : next.end as CalendarDate;
      setLocalRange({
        start,
        end
      });
      if (!isDesktop) {
        requestAnimationFrame(() => {
          if (dateActionsRef.current) {
            scrollClearOfStickyFooter(dateActionsRef.current);
          }
        });
      }
    };
    $[41] = isDesktop;
    $[42] = originalDuration;
    $[43] = setLocalRange;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== calendarKey || $[46] !== isEditingLocked || $[47] !== t16 || $[48] !== t17 || $[49] !== t18 || $[50] !== t20 || $[51] !== t21 || $[52] !== t22) {
    t23 = <div className={styles.calendar}><RangeCalendar key={calendarKey} aria-label={t16} monthsToShow={t17} layout={t18} variant="connected" minValue={t19} value={t20} isDisabled={isEditingLocked} fixedDuration={t21} onChange={t22} /></div>;
    $[45] = calendarKey;
    $[46] = isEditingLocked;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== clearDates || $[55] !== confirmDates || $[56] !== datesConfirmed || $[57] !== hasSelectedRange || $[58] !== isReadOnly || $[59] !== isTripUpdating || $[60] !== localRange || $[61] !== t || $[62] !== tForms) {
    t24 = !isReadOnly && <div className={styles.dateActions} ref={dateActionsRef}><button type="button" className={styles.clearDates} onClick={clearDates}>{t("booking_clear_dates")}</button><Button variant="primary" isPillShape={true} size="small" onPress={() => {
        trackFunnelEvent("trip_funnel_confirm_dates", {
          location: "booking_details"
        });
        confirmDates();
      }} isDisabled={!hasSelectedRange || isTripUpdating || datesConfirmed && localRange === null}>{tForms("confirm_dates")}</Button></div>;
    $[54] = clearDates;
    $[55] = confirmDates;
    $[56] = datesConfirmed;
    $[57] = hasSelectedRange;
    $[58] = isReadOnly;
    $[59] = isTripUpdating;
    $[60] = localRange;
    $[61] = t;
    $[62] = tForms;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== t12 || $[65] !== t14 || $[66] !== t15 || $[67] !== t23 || $[68] !== t24) {
    t25 = <div className={styles.group} id="overview-booking-dates">{t12}{t14}{t15}{t23}{t24}</div>;
    $[64] = t12;
    $[65] = t14;
    $[66] = t15;
    $[67] = t23;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className={`${styles.divider} ${styles.desktopHidden}`} aria-hidden={true} />;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== t) {
    t27 = t("booking_travellers_question");
    $[71] = t;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== t27) {
    t28 = <p className={styles.question}>{t27}</p>;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== t) {
    t29 = t("booking_choose_travellers");
    $[75] = t;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  let t30;
  if ($[77] !== setIsTravellersModalOpen) {
    t30 = () => {
      trackClickEvent("trip-page-edit-click-pax");
      setIsTravellersModalOpen(true);
    };
    $[77] = setIsTravellersModalOpen;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  let t31;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <span className={styles.rowIcon} aria-hidden={true}><User decorative={true} size="large" /></span>;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== selectedAdultCount || $[81] !== selectedChildCount || $[82] !== selectedRooms || $[83] !== t) {
    t32 = t("booking_travellers_summary", {
      adults: selectedAdultCount,
      children: selectedChildCount,
      rooms: selectedRooms
    });
    $[80] = selectedAdultCount;
    $[81] = selectedChildCount;
    $[82] = selectedRooms;
    $[83] = t;
    $[84] = t32;
  } else {
    t32 = $[84];
  }
  let t33;
  if ($[85] !== t32) {
    t33 = <span className={styles.rowValue}>{t32}</span>;
    $[85] = t32;
    $[86] = t33;
  } else {
    t33 = $[86];
  }
  let t34;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <span className={styles.rowChevron} aria-hidden={true}><NavArrowDown decorative={true} size="small" /></span>;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== isEditingLocked || $[89] !== t29 || $[90] !== t30 || $[91] !== t33) {
    t35 = <button type="button" className={styles.selectRow} aria-label={t29} disabled={isEditingLocked} onClick={t30}>{t31}{t33}{t34}</button>;
    $[88] = isEditingLocked;
    $[89] = t29;
    $[90] = t30;
    $[91] = t33;
    $[92] = t35;
  } else {
    t35 = $[92];
  }
  let t36;
  if ($[93] !== t28 || $[94] !== t35) {
    t36 = <div className={`${styles.group} ${styles.desktopHidden}`}>{t28}{t35}</div>;
    $[93] = t28;
    $[94] = t35;
    $[95] = t36;
  } else {
    t36 = $[95];
  }
  let t37;
  if ($[96] !== t25 || $[97] !== t36 || $[98] !== t9) {
    t37 = <div className={styles.stack}>{t9}{t10}{t25}{t26}{t36}</div>;
    $[96] = t25;
    $[97] = t36;
    $[98] = t9;
    $[99] = t37;
  } else {
    t37 = $[99];
  }
  let t38;
  if ($[100] !== t) {
    t38 = t("overview_pricing_heading");
    $[100] = t;
    $[101] = t38;
  } else {
    t38 = $[101];
  }
  let t39;
  if ($[102] !== t38) {
    t39 = <h3 className={styles.desktopPricingTitle}>{t38}</h3>;
    $[102] = t38;
    $[103] = t39;
  } else {
    t39 = $[103];
  }
  let t40;
  if ($[104] !== t) {
    t40 = t("overview_pricing_description_1");
    $[104] = t;
    $[105] = t40;
  } else {
    t40 = $[105];
  }
  let t41;
  if ($[106] !== t40) {
    t41 = <p className={styles.desktopPricingText}>{t40}</p>;
    $[106] = t40;
    $[107] = t41;
  } else {
    t41 = $[107];
  }
  let t42;
  if ($[108] !== t) {
    t42 = t("overview_pricing_description_2");
    $[108] = t;
    $[109] = t42;
  } else {
    t42 = $[109];
  }
  let t43;
  if ($[110] !== t42) {
    t43 = <p className={styles.desktopPricingText}>{t42}</p>;
    $[110] = t42;
    $[111] = t43;
  } else {
    t43 = $[111];
  }
  let t44;
  if ($[112] !== t39 || $[113] !== t41 || $[114] !== t43) {
    t44 = <aside className={styles.desktopPricingBox}>{t39}{t41}{t43}</aside>;
    $[112] = t39;
    $[113] = t41;
    $[114] = t43;
    $[115] = t44;
  } else {
    t44 = $[115];
  }
  let t45;
  if ($[116] !== t37 || $[117] !== t44) {
    t45 = <div className={styles.desktopLayout}>{t37}{t44}</div>;
    $[116] = t37;
    $[117] = t44;
    $[118] = t45;
  } else {
    t45 = $[118];
  }
  let t46;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <div className={styles.divider} aria-hidden={true} />;
    $[119] = t46;
  } else {
    t46 = $[119];
  }
  let t47;
  if ($[120] !== setIsOriginModalOpen) {
    t47 = () => setIsOriginModalOpen(false);
    $[120] = setIsOriginModalOpen;
    $[121] = t47;
  } else {
    t47 = $[121];
  }
  let t48;
  if ($[122] !== confirmOrigin || $[123] !== isOriginModalOpen || $[124] !== selectedOrigin || $[125] !== t47 || $[126] !== tripId) {
    t48 = <ChangeOriginModal isOpen={isOriginModalOpen} close={t47} currentOrigin={selectedOrigin} tripId={tripId} onConfirm={confirmOrigin} />;
    $[122] = confirmOrigin;
    $[123] = isOriginModalOpen;
    $[124] = selectedOrigin;
    $[125] = t47;
    $[126] = tripId;
    $[127] = t48;
  } else {
    t48 = $[127];
  }
  let t49;
  if ($[128] !== childAges) {
    t49 = childAges.join(",");
    $[128] = childAges;
    $[129] = t49;
  } else {
    t49 = $[129];
  }
  const t50 = `${adultCount}-${t49}-${selectedRooms}`;
  let t51;
  if ($[130] !== setIsTravellersModalOpen) {
    t51 = () => setIsTravellersModalOpen(false);
    $[130] = setIsTravellersModalOpen;
    $[131] = t51;
  } else {
    t51 = $[131];
  }
  let t52;
  if ($[132] !== adultCount || $[133] !== childAges || $[134] !== confirmTravellers || $[135] !== dateRangeEnd || $[136] !== isTravellersModalOpen || $[137] !== selectedRooms || $[138] !== t50 || $[139] !== t51) {
    t52 = <ChangeTravellersModal key={t50} isOpen={isTravellersModalOpen} close={t51} adultCount={adultCount} initialChildAges={childAges} rooms={selectedRooms} tripEndDateLabel={dateRangeEnd} onConfirm={confirmTravellers} />;
    $[132] = adultCount;
    $[133] = childAges;
    $[134] = confirmTravellers;
    $[135] = dateRangeEnd;
    $[136] = isTravellersModalOpen;
    $[137] = selectedRooms;
    $[138] = t50;
    $[139] = t51;
    $[140] = t52;
  } else {
    t52 = $[140];
  }
  let t53;
  if ($[141] !== t1 || $[142] !== t45 || $[143] !== t48 || $[144] !== t52) {
    t53 = <section id="overview-booking-details" className={styles.section} aria-labelledby="trip-v2-booking-heading">{t1}{t45}{t46}{t48}{t52}</section>;
    $[141] = t1;
    $[142] = t45;
    $[143] = t48;
    $[144] = t52;
    $[145] = t53;
  } else {
    t53 = $[145];
  }
  return t53;
}